import { ArticlesList } from '@/components/orders/show/ArticlesList'
import { CustomerBlock } from '@/components/orders/show/CustomerBlock'
import { HandoverStep, HandoverTimeline } from '@/components/orders/show/HandoverTimeline'
import { OrderHeader } from '@/components/orders/show/OrderHeader'
import { StatusRow } from '@/components/orders/show/StatusRow'
import { useOrderActions } from '@/hooks/useOrderActions'
import { Order, SharedProps } from '@/types'
import { latestVerifiedAt, latestVerifier } from '@/utils/format'
import { Head, usePage } from '@inertiajs/react'
import { Badge, Button, Card, Divider, Stack } from '@mantine/core'
import { IconCalendarUser, IconMessage } from '@tabler/icons-react'
import dayjs from 'dayjs'

export default function BoutiqueOrderShow({ order }: { order: Order }) {
  const { current_organization } = usePage<SharedProps>().props
  const { confirmReception, markCompleted, handleSendSms } = useOrderActions(order.id, current_organization?.slug || '')
  if (!current_organization) return null

  const { id: orderId, sms_sent_at, completed_at, planned_date } = order

  const productionDone = order.status === 'ready' || order.status === 'completed'
  const isPickupContract = order.collected_by_provider
  const workDoneAt = latestVerifiedAt(order)

  const smsDescription = (() => {
    if (sms_sent_at) {
      return `Envoyé le ${dayjs(sms_sent_at).format('DD/MM/YYYY HH:mm')}${order.sms_sender ? ` (${order.sms_sender.name})` : ''}`
    }
    if (!productionDone) return 'En attente de production'
    if (!order.received_at) return 'En attente de réception en boutique'
    return 'Prêt à être envoyé'
  })()

  const steps: HandoverStep[] = []

  if (isPickupContract || order.collected_at) {
    steps.push({
      key: 'collected',
      label: 'Collectée par le coursier',
      date: order.collected_at,
      actorName: order.collector?.name,
      done: Boolean(order.collected_at),
    })
  }

  steps.push({
    key: 'production',
    label: 'Travail terminé en atelier',
    date: workDoneAt,
    actorName: latestVerifier(order),
    done: productionDone,
  })

  steps.push({
    key: 'delivered',
    label: 'Livrée en boutique',
    date: order.received_at,
    actorName: order.deliverer?.name,
    done: Boolean(order.received_at),
    action: order.delivered_by_id ? (
      <Button size="xs" variant="filled" onClick={confirmReception}>
        Confirmer la réception
      </Button>
    ) : undefined,
  })

  steps.push({
    key: 'picked-up',
    label: 'Récupérée par le client',
    date: completed_at,
    done: Boolean(completed_at),
    action: order.received_at ? (
      <Button size="xs" variant="filled" color="green" onClick={markCompleted}>
        Marquer comme complété
      </Button>
    ) : undefined,
  })

  const canSendSms = productionDone && Boolean(order.received_at) && !sms_sent_at

  return (
    <>
      <Head title={`Commande #${orderId}`} />

      <Card withBorder radius="md" padding="lg" shadow="sm">
        <Stack gap="lg">
          <OrderHeader order={order} organizationSlug={current_organization.slug} />

          <CustomerBlock name={order.customer_name} phoneNumber={order.customer_phone} />

          <Divider />

          <Stack gap="sm">
            <StatusRow
              icon={<IconCalendarUser width="70%" height="70%" />}
              color="dark"
              label="Date prévue"
              description="pour la collecte client"
              action={
                <Badge color="dark" variant="light">
                  {planned_date ? dayjs(planned_date).format('ddd, D MMM YYYY') : 'Non définie'}
                </Badge>
              }
            />

            <StatusRow
              icon={<IconMessage width="70%" height="70%" />}
              label="SMS"
              description={smsDescription}
              action={
                sms_sent_at ? (
                  <Badge color="green" variant="light">
                    SMS Envoyé
                  </Badge>
                ) : (
                  <Button size="xs" variant="light" onClick={handleSendSms} disabled={!canSendSms}>
                    Envoyer SMS
                  </Button>
                )
              }
            />
          </Stack>

          <HandoverTimeline title="Parcours de la commande" steps={steps} />

          <Divider />

          <ArticlesList order={order} organizationSlug={current_organization.slug} />
        </Stack>
      </Card>
    </>
  )
}
